import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner, { ButtonSpinner } from '../components/LoadingSpinner';
import usePageTitle from '@/hooks/usePageTitle';
import Modal from '../components/Modal';
import { calcNetScore, calcStrokesGiven } from '../utils/handicapUtils';
import { HoleStatsModel, RoundModel } from '@/models/RoundModels';
import { CourseModel } from '@/models/CourseModels';
import { CourseService } from '@/services/CourseService';
import { resolveType } from '@/services/ServiceContainer';
import { RoundService } from '@/services/RoundService';

interface ScoresOnlyProps { }

// interface Hole extends HoleInfo {
//     number: number;
//     par: number;
//     mensHandicap18: number;
//     mensHandicap9: number;
//     womensHandicap18: number | null;
//     womensHandicap9: number | null;
//     tees: Array<{
//         name: string;
//         yards: number;
//     }>;
// }

// interface CourseData {
//     scorecard: {
//         holes: Hole[];
//     };
// }

// interface RoundData extends Round {
//     setup: RoundSetup;
//     users: {
//         [userId: string]: {
//             uid: string;
//             encodedEmail: string;
//             displayName: string;
//             selectedTee: string;
//             handicap: number | null;
//             flightId: string | null;
//             holeStats: HoleStats[];
//             skinsAmount: number;
//         };
//     };
//     context: {
//         type: 'casual' | 'league' | 'tournament';
//         league?: {
//             id: string;
//             dayId: string;
//         };
//     };
//     updatedAt: Timestamp;
// }

const SCORES_ONLY_TUTORIAL_KEY = 'SCORES_ONLY_TUTORIAL_SHOWN';

const ScoresOnly: React.FC<ScoresOnlyProps> = () => {
    const { theme } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const { courseId, roundId } = useParams<{ courseId: string; roundId: string }>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [roundModel, setRoundModel] = useState<RoundModel | null>(null);
    const [courseModel, setCourseModel] = useState<CourseModel | null>(null);
    const [scores, setScores] = useState<{ [key: number]: string }>({});
    // const [grossScore, setGrossScore] = useState(0);
    // const [netScore, setNetScore] = useState(0);
    // const [relativeToPar, setRelativeToPar] = useState(0);
    const [completingRound, setCompletingRound] = useState(false);
    const [deletingRound, setDeletingRound] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        type: 'info' | 'warning' | 'error';
        onConfirm?: () => void;
        confirmText?: string;
    }>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
        confirmText: 'Confirm'
    });
    const [tutorialVisible, setTutorialVisible] = useState(() => {
        return !localStorage.getItem(SCORES_ONLY_TUTORIAL_KEY);
    });
    const [countdown, setCountdown] = useState(5);
    const [focusedHole, setFocusedHole] = useState<number | null>(null);
    const [isNavigating, setIsNavigating] = useState(false);

    // Add escape key handler for modal
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (!modalConfig.isOpen) return;

            if (e.key === 'Escape') {
                setModalConfig(prev => ({ ...prev, isOpen: false }));
            } else if (e.key === 'Enter' && modalConfig.onConfirm) {
                modalConfig.onConfirm();
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [modalConfig.isOpen, modalConfig.onConfirm]);

    // Set focus to first textbox on load
    useEffect(() => {
        if (!loading && courseModel) {
            const firstHole = roundModel?.setup.holesToPlay === 'back9' ? 10 : 1;
            const firstInput = document.querySelector(`input[data-hole="${firstHole}"]`) as HTMLInputElement;
            if (firstInput) {
                firstInput.focus();
                if (firstInput.value) {
                    firstInput.setSelectionRange(0, 1);
                }
            }
        }
    }, [loading, courseModel, roundModel]);

    // Set page title
    usePageTitle('Scores Only');

    const calculateScores = (newScores: { [key: number]: string }) => {
        if (!courseModel || !roundModel || !user) return;

        let gross = 0;
        let net = 0;
        let totalPar = 0;
        const handicap = roundModel.users[user.uid]?.handicap || 0;
        const holesToPlay = roundModel.setup.holesToPlay;

        // Get holes to calculate based on round type
        const holes = courseModel.scorecard.holes.filter(hole => {
            if (holesToPlay === 'front9') return hole.number <= 9;
            if (holesToPlay === 'back9') return hole.number > 9;
            return true;
        });

        // // Sort holes by handicap for net score calculation
        // const sortedHoles = [...holes].sort((a, b) => {
        //     const handicapA = holesToPlay === 'all18' ? a.mensHandicap18 : a.mensHandicap9;
        //     const handicapB = holesToPlay === 'all18' ? b.mensHandicap18 : b.mensHandicap9;
        //     return handicapA - handicapB;
        // });

        // Calculate gross score and total par
        holes.forEach(hole => {
            const score = parseInt(newScores[hole.number] || '0');
            if (score > 0) {
                gross += score;
                totalPar += hole.par;

                // Calculate net score
                net += calcNetScore({
                    holesToPlay: holesToPlay,
                    grossScore: score,
                    holeInfo: hole,
                    handicap: handicap
                }) || 0;
            }
        });


        // setGrossScore(gross);
        // setNetScore(net);
        // setRelativeToPar(gross - totalPar);
    };

    useEffect(() => {
        calculateScores(scores);
    }, [scores, courseModel, roundModel, user]);

    useEffect(() => {
        const loadData = async () => {
            if (!user || !courseId || !roundId) {
                setError('Missing required parameters');
                setLoading(false);
                return;
            }

            try {
                // Load course data
                const courseService = resolveType(CourseService);
                const courseModel = await courseService.get(courseId);
                setCourseModel(courseModel);

                // Load round data
                const roundService = resolveType(RoundService);
                const roundModel = await roundService.get(roundId);
                setRoundModel(roundModel);

                console.log('roundModel', roundModel);
                console.log('roundModel.setup.holesToPlay', roundModel?.setup?.holesToPlay);

                // Initialize scores object based on round type and existing holeStats
                const initialScores: { [key: number]: string } = {};
                const userData = roundModel!.users[user.uid];
                const existingHoleStats = userData?.holeStats || [];

                if (roundModel!.setup.holesToPlay === 'all18') {
                    for (let i = 1; i <= 18; i++) {
                        const holeStat = existingHoleStats.find(stat => stat.hole === i);
                        initialScores[i] = holeStat?.grossScore?.toString() || '';
                    }
                } else if (roundModel!.setup.holesToPlay === 'front9') {
                    for (let i = 1; i <= 9; i++) {
                        const holeStat = existingHoleStats.find(stat => stat.hole === i);
                        initialScores[i] = holeStat?.grossScore?.toString() || '';
                    }
                } else if (roundModel!.setup.holesToPlay === 'back9') {
                    for (let i = 10; i <= 18; i++) {
                        const holeStat = existingHoleStats.find(stat => stat.hole === i);
                        initialScores[i] = holeStat?.grossScore?.toString() || '';
                    }
                }
                setScores(initialScores);

            } catch (err) {
                console.error('Error loading data:', err);
                setError(err instanceof Error ? err.message : 'Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [user, courseId, roundId]);

    const handleScoreChange = (holeNumber: number, value: string) => {
        // Only allow single digits 1-9
        if (value === '' || /^[1-9]$/.test(value)) {
            // If the new value is the same as the existing value, move to next input
            if (value === scores[holeNumber]) {
                const nextHole = holeNumber + 1;
                const maxHole = roundModel?.setup.holesToPlay === 'all18' ? 18 :
                    roundModel?.setup.holesToPlay === 'front9' ? 9 : 18;

                if (nextHole <= maxHole) {
                    setTimeout(() => {
                        const nextInput = document.querySelector(`input[data-hole="${nextHole}"]`) as HTMLInputElement;
                        if (nextInput) {
                            nextInput.focus();
                        }
                    }, 0);
                }
                return;
            }

            setScores(prev => {
                const newScores = {
                    ...prev,
                    [holeNumber]: value
                };
                return newScores;
            });

            // If a valid digit was entered, focus the next input or reselect current if last
            if (/^[1-9]$/.test(value)) {
                const nextHole = holeNumber + 1;
                const maxHole = roundModel?.setup.holesToPlay === 'all18' ? 18 :
                    roundModel?.setup.holesToPlay === 'front9' ? 9 : 18;

                setTimeout(() => {
                    if (nextHole <= maxHole) {
                        const nextInput = document.querySelector(`input[data-hole="${nextHole}"]`) as HTMLInputElement;
                        if (nextInput) {
                            nextInput.focus();
                        }
                    } else {
                        // If this is the last input, reselect the text
                        const currentInput = document.querySelector(`input[data-hole="${holeNumber}"]`) as HTMLInputElement;
                        if (currentInput) {
                            currentInput.setSelectionRange(0, 1);
                        }
                    }
                }, 0);
            }
        }
    };

    const handleKeyDown = (holeNumber: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        // Prevent any non-numeric input
        if (!/^[1-9]$/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Enter') {
            e.preventDefault();
            return;
        }

        // Handle Enter key: move to next input or complete round
        if (e.key === 'Enter') {
            const maxHole = roundModel?.setup.holesToPlay === 'all18' ? 18 :
                roundModel?.setup.holesToPlay === 'front9' ? 9 : 18;
            const nextHole = holeNumber + 1;
            if (nextHole <= maxHole) {
                setTimeout(() => {
                    const nextInput = document.querySelector(`input[data-hole="${nextHole}"]`) as HTMLInputElement;
                    if (nextInput) {
                        nextInput.focus();
                        if (nextInput.value) {
                            nextInput.setSelectionRange(0, 1);
                        }
                    }
                }, 0);
            } else {
                // Last input: save scores and trigger complete
                saveScoresToLocal().then(() => {
                    handleFinishRound();
                });
            }
            e.preventDefault();
            return;
        }

        // Handle advancing on same digit if fully selected
        if (/^[1-9]$/.test(e.key)) {
            const input = e.target as HTMLInputElement;
            if (
                input.value === e.key &&
                input.selectionStart === 0 &&
                input.selectionEnd === input.value.length
            ) {
                // Advance to next input
                const maxHole = roundModel?.setup.holesToPlay === 'all18' ? 18 :
                    roundModel?.setup.holesToPlay === 'front9' ? 9 : 18;
                const nextHole = holeNumber + 1;
                if (nextHole <= maxHole) {
                    setTimeout(() => {
                        const nextInput = document.querySelector(`input[data-hole="${nextHole}"]`) as HTMLInputElement;
                        if (nextInput) {
                            nextInput.focus();
                            if (nextInput.value) {
                                nextInput.setSelectionRange(0, 1);
                            }
                        }
                    }, 0);
                }
                e.preventDefault();
                return;
            }
        }

        // Handle backspace/delete when input is empty
        if ((e.key === 'Backspace' || e.key === 'Delete') && !scores[holeNumber]) {
            const prevHole = holeNumber - 1;
            const minHole = roundModel?.setup.holesToPlay === 'back9' ? 10 : 1;

            if (prevHole >= minHole) {
                e.preventDefault();
                // Clear the previous input
                setScores(prev => ({
                    ...prev,
                    [prevHole]: ''
                }));
                // Focus the previous input
                setTimeout(() => {
                    const prevInput = document.querySelector(`input[data-hole="${prevHole}"]`) as HTMLInputElement;
                    if (prevInput) {
                        prevInput.focus();
                        // Select the digit if it exists
                        if (prevInput.value) {
                            prevInput.setSelectionRange(0, 1);
                        }
                    }
                }, 0);
            }
        }

        // Handle arrow key navigation
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            const maxHole = roundModel?.setup.holesToPlay === 'all18' ? 18 :
                roundModel?.setup.holesToPlay === 'front9' ? 9 : 18;
            const minHole = roundModel?.setup.holesToPlay === 'back9' ? 10 : 1;

            let targetHole: number;
            if (e.key === 'ArrowLeft') {
                targetHole = holeNumber - 1;
                if (targetHole < minHole) return;
            } else {
                targetHole = holeNumber + 1;
                if (targetHole > maxHole) return;
            }

            setTimeout(() => {
                const targetInput = document.querySelector(`input[data-hole="${targetHole}"]`) as HTMLInputElement;
                if (targetInput) {
                    targetInput.focus();
                    // Select the digit if it exists
                    if (targetInput.value) {
                        targetInput.setSelectionRange(0, 1);
                    }
                }
            }, 0);
        }
    };

    // const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    //     // Select the digit if it exists when the input is focused
    //     if (e.target.value) {
    //         e.target.setSelectionRange(0, 1);
    //     }
    // };

    const renderScoreInputs = (startHole: number, endHole: number) => {
        const holes = Array.from({ length: endHole - startHole + 1 }, (_, i) => startHole + i);
        const userHandicap = roundModel?.users[user?.uid || '']?.handicap;
        const holesToPlay = roundModel?.setup?.holesToPlay || 'front9';

        return (
            <div className="score-grid">
                {holes.map(holeNumber => {
                    const holeInfo = courseModel?.scorecard.holes.find(h => h.number === holeNumber);
                    let numDots = 0;
                    if (typeof userHandicap === 'number' && holeInfo) {
                        numDots = calcStrokesGiven(userHandicap, holeInfo, holesToPlay);
                    }
                    return (
                        <div key={holeNumber} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '4px',
                            position: 'relative',
                            minWidth: '40px',
                            minHeight: '40px'
                        }}>
                            {/* Dots above hole number if strokes given > 0 */}
                            <div style={{ height: '10px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '2px' }}>
                                {numDots > 0 && Array.from({ length: numDots }).map((_, i) => (
                                    <span key={i} style={{
                                        display: 'inline-block',
                                        width: '7px',
                                        height: '7px',
                                        borderRadius: '50%',
                                        background: theme.colors.BLACK,
                                        marginBottom: '2px'
                                    }} />
                                ))}
                            </div>
                            <div style={{
                                fontSize: '1.1em',
                                fontWeight: 'bold',
                                color: theme.colors.TEXT,
                                lineHeight: '1'
                            }}>
                                {holeNumber}
                            </div>
                            <div style={{
                                fontSize: '0.65em',
                                color: theme.colors.TEXT_SECONDARY,
                                lineHeight: '1'
                            }}>
                                Par {holeInfo?.par || '-'}
                            </div>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={scores[holeNumber] || ''}
                                data-hole={holeNumber}
                                onChange={(e) => handleScoreChange(holeNumber, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(holeNumber, e)}
                                onFocus={(e) => {
                                    e.target.select();
                                    setFocusedHole(holeNumber);
                                }}
                                onBlur={() => setFocusedHole(null)}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    textAlign: 'center',
                                    fontSize: '1.2em',
                                    fontWeight: 'bold',
                                    border: focusedHole === holeNumber
                                        ? `2px solid ${theme.colors.BUTTON_SECONDARY_BG}`
                                        : `1px solid ${theme.colors.BORDER}`,
                                    borderRadius:
                                        scores[holeNumber] && parseInt(scores[holeNumber]) < (holeInfo?.par || 0)
                                            ? '50%'
                                            : '4px',
                                    backgroundColor: scores[holeNumber] ?
                                        (parseInt(scores[holeNumber]) < (holeInfo?.par || 0) ? theme.colors.UNDER_PAR :
                                            parseInt(scores[holeNumber]) === (holeInfo?.par || 0) ? theme.colors.PAR :
                                                theme.colors.OVER_PAR) :
                                        'transparent',
                                    color: scores[holeNumber] ?
                                        (parseInt(scores[holeNumber]) < (holeInfo?.par || 0) ? theme.colors.UNDER_PAR_TEXT :
                                            parseInt(scores[holeNumber]) === (holeInfo?.par || 0) ? theme.colors.PAR_TEXT :
                                                theme.colors.OVER_PAR_TEXT) :
                                        theme.colors.TEXT,
                                    padding: 0,
                                    margin: 0,
                                    outline: 'none',
                                    cursor: 'pointer',
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'textfield',
                                    boxShadow: focusedHole === holeNumber
                                        ? `0 0 0 2px ${theme.colors.BUTTON_SECONDARY_BG}33` // 20% opacity for subtle glow
                                        : 'none'
                                }}
                            />
                        </div>
                    );
                })}
            </div>
        );
    };

    const saveScoresToLocal = async () => {
        if (!roundModel || !user || !courseModel) return;

        const updatedRoundData = { ...roundModel };
        const userData = updatedRoundData.users[user.uid];
        if (!userData) return;

        // Convert scores to holeStats format
        const holeStats: HoleStatsModel[] = Object.entries(scores)
            .map(([holeNumber, score]) => {
                const holeInfo = courseModel.scorecard.holes.find(h => h.number === parseInt(holeNumber));
                if (!holeInfo) return null;

                const grossScore = score ? parseInt(score) : null;
                const netScore = grossScore ? calcNetScore({
                    holesToPlay: roundModel.setup.holesToPlay,
                    grossScore,
                    holeInfo,
                    handicap: userData.handicap || 0
                }) : null;

                const stats: HoleStatsModel = {
                    hole: parseInt(holeNumber),
                    par: holeInfo.par,
                    grossScore,
                    grossScoreAdjusted: Math.min(grossScore || 0, holeInfo.par + 2),
                    netScore,
                    fairwayHit: null,
                    greenInRegulation: null,
                    putts: null,
                    chips: null,
                    penalties: null
                };

                return stats;
            })
            .filter((stat): stat is HoleStatsModel => stat !== null);

        userData.holeStats = holeStats;
        updatedRoundData.users[user.uid] = userData;

        // Save to local storage
        const roundService = resolveType(RoundService);
        await roundService.update(roundId!, user.uid, updatedRoundData, courseModel);

    };

    const handleFinishRound = async () => {
        if (!roundId || !user || !courseModel || !roundModel) return;

        // Check for extraordinary scores
        const extraordinaryScores: { hole: number; score: number; par: number; type: string }[] = [];
        Object.entries(scores).forEach(([holeNumber, score]) => {
            if (!score) return;
            const holeInfo = courseModel.scorecard.holes.find(h => h.number === parseInt(holeNumber));
            if (!holeInfo) return;

            const scoreNum = parseInt(score);
            const par = holeInfo.par;
            const relativeToPar = scoreNum - par;

            if (scoreNum === 1) {
                extraordinaryScores.push({ hole: parseInt(holeNumber), score: scoreNum, par, type: 'Hole in One' });
            } else if (relativeToPar === -3) {
                extraordinaryScores.push({ hole: parseInt(holeNumber), score: scoreNum, par, type: 'Albatross' });
            } else if (relativeToPar === -2) {
                extraordinaryScores.push({ hole: parseInt(holeNumber), score: scoreNum, par, type: 'Eagle' });
            }
        });

        if (extraordinaryScores.length > 0) {
            const message = `Extraordinary scores detected:\n\n${extraordinaryScores
                .map(score => `Hole ${score.hole} (Par ${score.par}): ${score.score} - ${score.type}`)
                .join('\n')}\n\nContinue?`;

            setModalConfig({
                isOpen: true,
                title: 'Extraordinary Scores',
                message,
                type: 'warning',
                confirmText: 'Continue',
                onConfirm: () => {
                    setModalConfig(prev => ({ ...prev, isOpen: false }));
                    checkMissingScores();
                }
            });
            return;
        }

        checkMissingScores();
    };

    const checkMissingScores = () => {
        // Check if there are any holes with no score
        const holesWithScore = Object.entries(scores).filter(([_, score]) => score !== '');
        const expectedHoles = roundModel?.setup.holesToPlay === 'all18' ? 18 : 9;

        if (holesWithScore.length < expectedHoles) {
            setModalConfig({
                isOpen: true,
                title: 'Missing Scores',
                message: `You have ${expectedHoles - holesWithScore.length} hole(s) with no score. Are you sure you want to complete this round?`,
                type: 'warning',
                confirmText: 'Continue',
                onConfirm: () => {
                    setModalConfig(prev => ({ ...prev, isOpen: false }));
                    completeRound();
                }
            });
            return;
        }

        completeRound();
    };

    const completeRound = async () => {
        if (!roundId || !user || !courseModel || !roundModel) return;

        try {
            setCompletingRound(true);

            // Save scores to round data first
            await saveScoresToLocal();

            // Force sync before completing the round
            const roundService = resolveType(RoundService);
            const updatedRoundModel = await roundService.get(roundId);
            if (!updatedRoundModel) {
                throw new Error('Failed to get updated round model');
            }
            await roundService.complete(updatedRoundModel, user.uid, courseModel);

            // Navigate to dashboard
            navigate('/dashboard');
        } catch (error) {
            console.error('Error finishing round:', error);
            setError('Failed to finish round');
            setCompletingRound(false);
        }
    };

    const handleDeleteRound = async () => {
        setModalConfig({
            isOpen: true,
            title: 'Delete Round',
            message: 'Are you sure you want to delete this round? This action cannot be undone.',
            type: 'error',
            onConfirm: async () => {
                setConfirming(true);
                try {
                    setDeletingRound(true);
                    const roundService = resolveType(RoundService);
                    await roundService.delete(roundId!, user!.uid);
                    navigate('/dashboard');
                } catch (error) {
                    console.error('Error deleting round:', error);
                    setModalConfig({
                        isOpen: true,
                        title: 'Error',
                        message: 'Failed to delete round. Please try again.',
                        type: 'error'
                    });
                } finally {
                    setDeletingRound(false);
                    setConfirming(false);
                }
            }
        });
    };

    // Add navigation handler for the scorecard button
    const handleScorecardClick = async () => {
        if (!courseId || !roundModel?.id) return;

        setIsNavigating(true);
        try {
            // Save scores before navigating
            await saveScoresToLocal();
            navigate(`/round-scorecard/${courseId}/${roundModel.id}`);
        } catch (error) {
            console.error('Error navigating to scorecard:', error);
        } finally {
            setIsNavigating(false);
        }
    };

    useEffect(() => {
        if (tutorialVisible) {
            const timer = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        setTutorialVisible(false);
                        localStorage.setItem(SCORES_ONLY_TUTORIAL_KEY, 'true');
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [tutorialVisible]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div style={{
                padding: theme.spacing.LARGE,
                color: theme.colors.ERROR,
                textAlign: 'center'
            }}>
                {error}
            </div>
        );
    }

    return (
        <>
            <style>
                {`
                input[data-hole]::selection {
                    color: inherit;
                }
                .score-grid {
                    display: grid;
                    grid-template-columns: repeat(9, 1fr);
                    gap: 4px;
                    row-gap: 16px;
                    justify-content: center;
                    max-width: 100%;
                    padding: 0 8px 16px 8px;
                }
                @media (max-width: 400px) {
                    .score-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }
                `}
            </style>
            <div style={{
                minHeight: '100vh',
                backgroundColor: theme.colors.BACKGROUND,
                color: theme.colors.TEXT,
                padding: theme.spacing.MEDIUM
            }}>
                <div style={{
                    marginBottom: theme.spacing.LARGE,
                    padding: theme.spacing.MEDIUM,
                    backgroundColor: theme.colors.SURFACE,
                    borderRadius: theme.borderRadius.MEDIUM,
                    border: `1px solid ${theme.colors.BORDER}`
                }}>
                    <h1 style={{
                        color: theme.colors.TEXT,
                        marginBottom: theme.spacing.SMALL,
                        fontSize: '1.5em',
                        fontWeight: 'bold'
                    }}>
                        {roundModel?.setup?.courseName}
                    </h1>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: theme.spacing.SMALL
                    }}>
                        <div style={{
                            color: theme.colors.TEXT,
                            fontSize: '1.2em',
                            fontWeight: 'bold'
                        }}>
                            {roundModel?.setup.holesToPlay === 'all18' ? '18 Holes' :
                                roundModel?.setup.holesToPlay === 'front9' ? 'Front 9' : 'Back 9'}
                        </div>

                        <div style={{
                            color: theme.colors.TEXT,
                            fontSize: '1.2em',
                            fontWeight: 'bold'
                        }}>
                            {(() => {
                                return roundModel?.startedAt ? (
                                    typeof roundModel?.startedAt.toDate === 'function'
                                        ? roundModel?.startedAt.toDate().toLocaleDateString()
                                        : new Date(roundModel?.startedAt.seconds * 1000).toLocaleDateString()
                                ) : 'Date not available';
                            })()}
                        </div>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{
                            color: theme.colors.TEXT,
                            fontSize: '1em',
                            fontWeight: 'bold'
                        }}>
                            {roundModel?.users[user?.uid || '']?.displayName}
                        </div>

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.SMALL
                        }}>
                            <div style={{
                                color: theme.colors.TEXT_SECONDARY,
                                fontSize: '1em'
                            }}>
                                Hcp:
                            </div>
                            <div style={{
                                color: theme.colors.TEXT,
                                fontSize: '1em',
                                fontWeight: 'bold'
                            }}>
                                {roundModel?.users[user?.uid || '']?.handicap || 'N/A'}
                            </div>
                        </div>
                    </div>
                </div>
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    padding: theme.spacing.MEDIUM
                }}>
                    {roundModel?.setup.holesToPlay === 'all18' && (
                        <>
                            {renderScoreInputs(1, 9)}
                            {renderScoreInputs(10, 18)}
                        </>
                    )}
                    {roundModel?.setup.holesToPlay === 'front9' && renderScoreInputs(1, 9)}
                    {roundModel?.setup.holesToPlay === 'back9' && renderScoreInputs(10, 18)}

                    {/* Score summary row (matches Dashboard League style) */}
                    {courseModel && roundModel && user && (() => {
                        // Calculate gross, net, and relative to par
                        const holes = courseModel.scorecard.holes.filter(hole => {
                            if (roundModel.setup.holesToPlay === 'front9') return hole.number <= 9;
                            if (roundModel.setup.holesToPlay === 'back9') return hole.number > 9;
                            return true;
                        });
                        let gross = 0;
                        let net = 0;
                        let totalPar = 0;
                        const handicap = roundModel.users[user.uid]?.handicap || 0;
                        holes.forEach(hole => {
                            const score = parseInt(scores[hole.number] || '0');
                            if (score > 0) {
                                gross += score;
                                totalPar += hole.par;
                                net += calcNetScore({
                                    holesToPlay: roundModel.setup.holesToPlay,
                                    grossScore: score,
                                    holeInfo: hole,
                                    handicap: handicap
                                }) || 0;
                            }
                        });
                        const toPar = gross - totalPar;
                        return (
                            <div style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginTop: theme.spacing.LARGE,
                                maxWidth: '400px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                padding: '0 24px',
                                alignItems: 'center',
                                borderRadius: '8px',
                                fontSize: '1.3em',
                                fontWeight: 500,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.03)'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing.MEDIUM
                                }}>
                                    <span style={{
                                        color: theme.colors.TEXT_SECONDARY,
                                        fontWeight: 'bold'
                                    }}>Gross:</span>
                                    <span style={{
                                        color: theme.colors.TEXT,
                                        fontWeight: 'bold',
                                        fontSize: '1.2em'
                                    }}>{gross}</span>
                                </div>
                                {toPar === 0 ? (
                                    <span style={{
                                        color: theme.colors.SUCCESS,
                                        fontWeight: 'bold',
                                        fontSize: '1.2em',
                                        width: '52px',
                                        height: '52px',
                                        margin: '0 4px',
                                        lineHeight: '24px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>E</span>
                                ) : (
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: theme.colors.WHITE,
                                        backgroundColor: toPar > 0 ? theme.colors.OVER_PAR : theme.colors.ERROR,
                                        fontWeight: 'bold',
                                        fontSize: '1.3em',
                                        width: '52px',
                                        height: '52px',
                                        borderRadius: toPar < 0 ? '50%' : '4px',
                                        margin: '0 4px',
                                        lineHeight: '24px',
                                    }}>{toPar > 0 ? `+${toPar}` : toPar}</span>
                                )}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing.MEDIUM
                                }}>
                                    <span style={{
                                        color: theme.colors.TEXT_SECONDARY,
                                        fontWeight: 'bold'
                                    }}>Net:</span>
                                    <span style={{
                                        color: theme.colors.TEXT,
                                        fontWeight: 'bold',
                                        fontSize: '1.2em'
                                    }}>{net}</span>
                                </div>
                            </div>
                        );
                    })()}

                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: theme.spacing.MEDIUM,
                        marginTop: theme.spacing.LARGE
                    }}>
                        <button
                            onClick={handleDeleteRound}
                            style={{
                                padding: theme.spacing.MEDIUM,
                                backgroundColor: theme.colors.ERROR,
                                color: theme.colors.WHITE,
                                border: 'none',
                                borderRadius: theme.borderRadius.MEDIUM,
                                cursor: deletingRound ? 'default' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '80px',
                                height: '54px',
                                opacity: deletingRound ? 0.7 : 1,
                                fontSize: '1rem',
                                fontWeight: '600'
                            }}
                            title="Delete Round"
                            disabled={deletingRound}
                        >
                            {deletingRound ? (
                                <ButtonSpinner size={24} color={theme.colors.BACKGROUND} />
                            ) : (
                                'Delete'
                            )}
                        </button>
                        <button
                            onClick={handleScorecardClick}
                            style={{
                                padding: theme.spacing.MEDIUM,
                                backgroundColor: theme.colors.BUTTON_SECONDARY_BG,
                                color: theme.colors.BUTTON_SECONDARY_TEXT,
                                border: 'none',
                                borderRadius: theme.borderRadius.MEDIUM,
                                cursor: isNavigating ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '80px',
                                height: '54px',
                                opacity: isNavigating ? 0.7 : 1,
                                fontSize: '1rem',
                                fontWeight: '600'
                            }}
                            title="View Scorecard"
                            disabled={isNavigating}
                        >
                            {isNavigating ? (
                                <ButtonSpinner size={24} color={theme.colors.BACKGROUND} />
                            ) : (
                                'Card'
                            )}
                        </button>
                        <button
                            onClick={handleFinishRound}
                            disabled={completingRound}
                            style={{
                                padding: theme.spacing.MEDIUM,
                                backgroundColor: theme.colors.BUTTON_PRIMARY_BG,
                                color: theme.colors.BUTTON_PRIMARY_TEXT,
                                border: 'none',
                                borderRadius: theme.borderRadius.MEDIUM,
                                cursor: completingRound ? 'not-allowed' : 'pointer',
                                fontSize: '1.25em',
                                width: '120px',
                                height: '54px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: theme.spacing.SMALL
                            }}
                        >
                            {completingRound ? (
                                <ButtonSpinner size={24} color={theme.colors.BACKGROUND} />
                            ) : 'Submit'}
                        </button>
                    </div>
                </div>

                {tutorialVisible && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999
                    }}>
                        <div style={{
                            backgroundColor: theme.colors.BACKGROUND,
                            padding: theme.spacing.LARGE,
                            borderRadius: theme.borderRadius.MEDIUM,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: theme.spacing.MEDIUM,
                            maxWidth: '80%',
                            textAlign: 'center'
                        }}>
                            <div style={{
                                color: theme.colors.TEXT,
                                fontSize: '1.2em',
                                marginBottom: theme.spacing.MEDIUM
                            }}>
                                Enter your scores for each hole. Use the number keys 1-9.
                            </div>
                            <div style={{
                                color: theme.colors.TEXT_SECONDARY,
                                fontSize: '1em'
                            }}>
                                Tutorial will dismiss in {countdown} seconds
                            </div>
                            <button
                                onClick={() => {
                                    setTutorialVisible(false);
                                    localStorage.setItem(SCORES_ONLY_TUTORIAL_KEY, 'true');
                                }}
                                style={{
                                    padding: theme.spacing.SMALL,
                                    backgroundColor: theme.colors.PRIMARY,
                                    color: theme.colors.BACKGROUND,
                                    border: 'none',
                                    borderRadius: theme.borderRadius.SMALL,
                                    cursor: 'pointer',
                                    marginTop: theme.spacing.MEDIUM
                                }}
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                )}

                {completingRound && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999
                    }}>
                        <div style={{
                            backgroundColor: theme.colors.BACKGROUND,
                            padding: theme.spacing.LARGE,
                            borderRadius: theme.borderRadius.MEDIUM,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: theme.spacing.MEDIUM
                        }}>
                            <LoadingSpinner />
                            <div style={{ color: theme.colors.TEXT }}>Submitting round...</div>
                        </div>
                    </div>
                )}

                <Modal
                    isOpen={modalConfig.isOpen}
                    title={modalConfig.title}
                    message={modalConfig.message}
                    type={modalConfig.type}
                    onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
                    onConfirm={modalConfig.onConfirm}
                    confirmText={confirming ? (
                        <ButtonSpinner size={20} color={theme.colors.BACKGROUND} />
                    ) : modalConfig.confirmText || 'Confirm'}
                    disabled={confirming}
                />
            </div>
        </>
    );
};

export default ScoresOnly; 